/**
 * The ground's surface as drawn, baked into a height grid (groundMesh.ts `GroundMesh.drawnAt`).
 *
 * `GroundMesh.surfaceAt` answers from the height formula, undoing the lateral wander with a fixed-point guess. The
 * sheet on screen is something else: a welded mesh of 0.25-unit quads whose vertices were each wandered by their own
 * cell's strength, so near a gorge's lip — where that strength steps from one cell to the next — the guess lands on
 * the wrong vertex and the answer jumps by centimetres to decimetres. A unit standing on the formula sank into the
 * drawn sheet or hovered over it there. This bakes the drawn triangles themselves: each grid sample takes the height
 * of the triangle it lies in, and a lookup is a bilinear read. No allocation per lookup.
 */
export class SurfaceRaster {
  private constructor(
    private readonly x0: number, private readonly z0: number,
    private readonly cols: number, private readonly rows: number,
    private readonly inv: number, private readonly data: Float32Array,
  ) {}

  /**
   * Rasterises the triangles (`pos` xyz triples, `idx` triangle indices, world space) over [x0, x1] × [z0, z1] at
   * `step` world units. A sample no triangle covers (a chasm, a lava pool) stays NaN.
   */
  static bake(pos: ArrayLike<number>, idx: ArrayLike<number>, x0: number, z0: number, x1: number, z1: number, step: number): SurfaceRaster {
    const inv = 1 / step;
    const cols = Math.floor((x1 - x0) * inv) + 1, rows = Math.floor((z1 - z0) * inv) + 1;
    const data = new Float32Array(cols * rows).fill(NaN);
    for (let t = 0; t + 2 < idx.length; t += 3) {
      const a = idx[t] * 3, b = idx[t + 1] * 3, c = idx[t + 2] * 3;
      const ax = pos[a], az = pos[a + 2], bx = pos[b], bz = pos[b + 2], cx = pos[c], cz = pos[c + 2];
      const det = (bz - cz) * (ax - cx) + (cx - bx) * (az - cz);
      if (Math.abs(det) < SurfaceRaster.MinArea) continue;
      const i0 = Math.max(0, Math.ceil((Math.min(ax, bx, cx) - x0) * inv)), i1 = Math.min(cols - 1, Math.floor((Math.max(ax, bx, cx) - x0) * inv));
      const j0 = Math.max(0, Math.ceil((Math.min(az, bz, cz) - z0) * inv)), j1 = Math.min(rows - 1, Math.floor((Math.max(az, bz, cz) - z0) * inv));
      for (let j = j0; j <= j1; j++) {
        const z = z0 + j * step;
        for (let i = i0; i <= i1; i++) {
          const x = x0 + i * step;
          const u = ((bz - cz) * (x - cx) + (cx - bx) * (z - cz)) / det;
          const v = ((cz - az) * (x - cx) + (ax - cx) * (z - cz)) / det;
          const w = 1 - u - v;
          if (u < -SurfaceRaster.Edge || v < -SurfaceRaster.Edge || w < -SurfaceRaster.Edge) continue;
          data[j * cols + i] = u * pos[a + 1] + v * pos[b + 1] + w * pos[c + 1];
        }
      }
    }
    return new SurfaceRaster(x0, z0, cols, rows, inv, data);
  }

  /** Triangles thinner than this (twice their area, world units²) are skipped: a folded sliver has no height to give. */
  static readonly MinArea = 1e-9;
  /** Barycentric slack, so a sample exactly on a shared edge is never missed by both triangles. */
  static readonly Edge = 1e-6;

  /** True when (x, z) is inside the baked area (whether or not ground is drawn there). */
  covers(x: number, z: number): boolean {
    const fx = (x - this.x0) * this.inv, fz = (z - this.z0) * this.inv;
    return fx >= 0 && fz >= 0 && fx <= this.cols - 1 && fz <= this.rows - 1;
  }

  /** The drawn height at (x, z), bilinear between the samples around it that carry ground; NaN off the grid or over a hole. */
  at(x: number, z: number): number {
    const fx = (x - this.x0) * this.inv, fz = (z - this.z0) * this.inv;
    if (fx < 0 || fz < 0 || fx > this.cols - 1 || fz > this.rows - 1) return NaN;
    const i = Math.min(Math.floor(fx), this.cols - 2), j = Math.min(Math.floor(fz), this.rows - 2);
    const tx = fx - i, tz = fz - j, k = j * this.cols + i, d = this.data, n = this.cols;
    // bilinear over the corners that carry ground: at a chasm's lip some do not, and the sheet still runs to the edge
    let sum = 0, wsum = 0;
    for (let c = 0; c < 4; c++) {
      const y = d[k + (c & 1) + (c >> 1) * n];
      if (Number.isNaN(y)) continue;
      const w = ((c & 1) ? tx : 1 - tx) * ((c >> 1) ? tz : 1 - tz);
      sum += y * w; wsum += w;
    }
    return wsum > SurfaceRaster.MinWeight ? sum / wsum : NaN;   // NaN over a hole
  }

  /** Below this share of the four corners' weight the point is over a hole, not beside one. */
  static readonly MinWeight = 0.05;
}
